import React, { useEffect, useState } from "react";
import { getBrowserId, getParamIfExists, reverseGeocode } from "@/utils";
import { useTranslation } from "react-i18next";
import { apiScanQRCode } from "@/api";
import { useLocation, useNavigate } from "react-router-dom";

import { useScanQrcodeStore, useSettingStore } from "@/store";
import { TEMPLATE_TYPES, STAMP_STATUS, STATUS_VERIFY, VERIFICATION_TYPE } from "@/constants";

import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

import TemplateOne from "@/components/templates/verifies/TemplateOne";

const HomeView: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);

  const { setIsSerial, setParams, getTemplateCode, setDataScanQrcode, setVerificationLocation } = useScanQrcodeStore();
  const { isFetchInit } = useSettingStore();

  // Function để lấy tọa độ từ trình duyệt (non-blocking)
  const tryGetCurrentLocation = (callback: (coords: { lat: string; lon: string } | null) => void) => {
    if (!navigator.geolocation) {
      console.warn("Geolocation is not supported by this browser");
      callback(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        callback({
          lat: position.coords.latitude.toString(),
          lon: position.coords.longitude.toString(),
        });
      },
      (error) => {
        console.warn("Error getting location:", error.message);
        callback(null);
      },
      {
        enableHighAccuracy: false, // Tắt high accuracy để nhanh hơn
        timeout: 3000, // Giảm timeout xuống 3s
        maximumAge: 300000, // Cache 5 phút
      }
    );
  };

  useEffect(() => {
    const initializeApp = async () => {
      if (!isFetchInit) {
        setTimeout(() => {
          if (isFetchInit) {
            initializeApp();
          }
        }, 500);
        return;
      }

      const searchParams = new URLSearchParams(location.search);

      // Build params object with only existing values
      const rawParams = {
        xid: getParamIfExists(searchParams, "xid"),
        serial: getParamIfExists(searchParams, "serial"),
        ser: getParamIfExists(searchParams, "ser"),
        type: getParamIfExists(searchParams, "type"),
        user_uuid: getParamIfExists(searchParams, "user_uuid"),
        preview: getParamIfExists(searchParams, "preview"),
        utm: getParamIfExists(searchParams, "utm"),
        lat: getParamIfExists(searchParams, "lat"),
        lon: getParamIfExists(searchParams, "lon"),
      };

      // Filter out undefined values to create clean params object
      const params = Object.fromEntries(Object.entries(rawParams).filter(([_, value]) => value !== undefined));
      setParams(params as any);

      // Lấy location trong background (non-blocking) nếu chưa có trong params
      const hasLatLon = searchParams.has("lat") && searchParams.has("lon");
      if (!hasLatLon) {
        tryGetCurrentLocation((coords) => {
          if (coords) {
            params.lat = coords.lat;
            params.lon = coords.lon;
            setParams(params as any);
          }
        });
      }

      const serialx = params.serial || params.ser;
      const scanType = params.type || VERIFICATION_TYPE.LANDING_PAGE;
      const browserId = getBrowserId();

      setIsSerial(!!serialx);

      // Golden Pen là company mặc định (single-tenant): khi vào trang không có
      // tham số quét nào (serial/ser/xid) thì hiển thị form kích hoạt bảo hành
      // mặc định thay vì rơi vào luồng scan rỗng -> /not-found.
      if (!serialx && !params.xid) {
        navigate("/kichhoat", { replace: true });
        return;
      }

      // No serial - nothing to do
      // if (!serialx) {
      //   setIsLoading(false);
      //   return;
      // }

      // Has serial - scan QR code
      try {
        // Build API payload with only existing values
        const apiPayload: Record<string, string> = {
          serial: serialx || "",
          type: scanType,
          browser_id: browserId,
        };

        // Add optional params only if they exist
        if (params.xid) apiPayload.xid = params.xid;
        if (params.preview) apiPayload.preview = params.preview;
        if (params.user_uuid) apiPayload.user_uuid = params.user_uuid;
        if (params.utm) apiPayload.utm = params.utm;
        if (params.lat) apiPayload.lat = params.lat;
        if (params.lon) apiPayload.lon = params.lon;

        const response = await apiScanQRCode(apiPayload);
        const dataResponse = response.data;

        // Check stamp status
        const status: any = dataResponse?.stamp_code?.status || "";
        if (status === "not_found") {
          navigate("/not-found");
          return;
        }

        // Chuyển đổi template linh hoạt: scan-setting của công ty chọn
        // mrw_template_4 (Golden Pen — Warranty Barcode) thì mọi URL scan chuẩn
        // (xid/serial) cũng chuyển sang luồng bảo hành /kichhoat.
        // Lưu ý: BE ẩn verification_code với tem chưa bán / stamp_type != auto —
        // khi đó chỉ truyền series, /kichhoat sẽ hiện form nhập Mã cào thủ công.
        if (dataResponse?.template?.code === TEMPLATE_TYPES.TEMPLATE_4) {
          const warrantyParams = new URLSearchParams();
          const stampSerial = dataResponse?.stamp_code?.serial || "";
          const verificationCode = dataResponse?.stamp_code?.verification_code || "";
          if (stampSerial) warrantyParams.set("series", stampSerial);
          if (verificationCode) warrantyParams.set("code", verificationCode);
          if (params.preview) warrantyParams.set("preview", "1");
          navigate(`/kichhoat?${warrantyParams.toString()}`, { replace: true });
          return;
        }

        // Save data to store
        setDataScanQrcode(dataResponse);

        // Bổ sung full_address nếu verification_location có lat/lon nhưng thiếu full_address
        const verificationLoc = dataResponse?.verification_location;
        if (verificationLoc && !verificationLoc.full_address && verificationLoc.latitude && verificationLoc.longitude) {
          // Lấy địa chỉ trong background (non-blocking)
          reverseGeocode(verificationLoc.latitude, verificationLoc.longitude).then((address) => {
            if (address) {
              const updatedLocation = { ...verificationLoc, full_address: address };
              setVerificationLocation(updatedLocation);
            }
          });
        }
        // Handle error statuses
        const hasStampError = [
          STAMP_STATUS.NEW,
          STAMP_STATUS.LOCKED,
          STAMP_STATUS.BLOCKED,
          STATUS_VERIFY.CANNOT_ACCESS,
        ].includes(status);

        if (hasStampError) {
          navigate("/stamp-error");
          return;
        }
      } catch (error) {
        navigate("/error");
      } finally {
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
      }
    };

    initializeApp();
  }, [location.search, navigate, setDataScanQrcode, isFetchInit]);

  const templateCode = getTemplateCode();

  const renderTemplate = () => {
    switch (templateCode) {
      case TEMPLATE_TYPES.TEMPLATE_1:
        return <TemplateOne />;
      // mrw_template_4 (Golden Pen — Warranty Barcode) đã được redirect sang
      // `/kichhoat` ngay trong initializeApp (trước khi render), không rơi xuống đây.
      default:
        return <TemplateOne />;
    }
  };

  if (isLoading) {
    return (
      <div className="qrx-container m-auto overflow-hidden">
        <div className="flex justify-center items-center min-h-screen">
          <div className="loading-overlay flex flex-col items-center justify-center gap-2">
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
            <div className="loading-text">{t("common.loading")}</div>
          </div>
        </div>
      </div>
    );
  }

  return <div className="qrx-container m-auto overflow-hidden">{renderTemplate()}</div>;
};

export default HomeView;
